import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Key } from '@mui/icons-material'
import { Button, Stack } from '@mui/material'
import { useStytchB2BClient } from '@stytch/react/b2b'
import { getRedirectUrl } from '@/utils/auth'
import { showErrorSnackbar } from '@/utils/snackbars'
import { PixyDocsForm } from '@/components/forms'
import { Email, LoginViewOptions } from './Login'

type FormValues = {
  email: string
}

export default function LoginWithSSO({
  stytchOrg,
  resetView,
  setShowOnly,
  ...rest
}: LoginViewOptions) {
  const stytch = useStytchB2BClient()

  const [view, setView] = useState<'login' | 'continue'>('login')
  const methods = useForm<FormValues>({
    defaultValues: {
      email: '',
    },
  })
  const {
    formState: { isDirty, isValid },
  } = methods

  const handleOnSubmit = useCallback(
    async (values: FormValues) => {
      try {
        if (stytchOrg) {
          await stytch.sso.start({
            connection_id: stytchOrg.sso_default_connection_id as string,
            login_redirect_url: getRedirectUrl(),
          })
        } else {
          const ssoResponse = await stytch.sso.discoverConnections(values.email)
          if (!ssoResponse.connections.length) {
            showErrorSnackbar(
              'No SSO connections found for this email. Please try a different email or contact support.',
            )
            return
          }
          await stytch.sso.start({
            connection_id: ssoResponse.connections[0].connection_id,
            login_redirect_url: getRedirectUrl(),
          })
        }
      } catch (e) {
        console.error('Error during sign in:', e)
        showErrorSnackbar(
          'An error occurred during the request. Please try again.',
        )
      }
    },
    [stytchOrg, stytch.sso],
  )

  useEffect(() => {
    if (resetView) {
      setView('login')
    }
  }, [resetView])

  return view === 'login' ? (
    <Button
      startIcon={<Key />}
      onClick={() => {
        setView('continue')
        setShowOnly('sso')
      }}
    >
      Continue with SSO
    </Button>
  ) : (
    <PixyDocsForm
      methods={methods}
      onSubmit={handleOnSubmit}
      style={{ width: '100%' }}
    >
      <Stack spacing={2}>
        <Email {...rest} />
        <Button type="submit" disabled={!isValid || !isDirty}>
          Continue
        </Button>
      </Stack>
    </PixyDocsForm>
  )
}
